import { useMemo } from 'react';
import { IEX_STATE_CODES } from '@constants/configs/states';
import { ApplicationStatus, IApplication, ALL_USER_ROLES, COVERAGE_YEAR, useAppSelector } from '../utils';

export const useShowEligibilityCriteria = (application: IApplication) => {
    const environment = useAppSelector(state => state?.common?.environment);
    const { userPermissionList } = useAppSelector(state => state.capApplications.capApplicationsInfo);

    const stateCode = environment?.stateCode;
    const activeRoleName = environment?.user?.activeModuleName?.toUpperCase();

    const isValidStateCode = useMemo(() => {
        return [
            IEX_STATE_CODES.ID,
            IEX_STATE_CODES.NJ,
            IEX_STATE_CODES.VA,
            IEX_STATE_CODES.PA,
            IEX_STATE_CODES.NV,
            IEX_STATE_CODES.GA,
            IEX_STATE_CODES.IL,
            IEX_STATE_CODES.GI,
            IEX_STATE_CODES.OR,
            IEX_STATE_CODES.NM,
            IEX_STATE_CODES.MN
        ].includes(stateCode);
    }, [stateCode]);

    const isValidRole = useMemo(() => {
        return [
            ALL_USER_ROLES.ADMIN, ALL_USER_ROLES.BA,
            ALL_USER_ROLES.SUPERVISOR, ALL_USER_ROLES.SUPERVISOR_SAH,
            ALL_USER_ROLES.L1_CSR, ALL_USER_ROLES.L2_CSR,
            ALL_USER_ROLES.L4_CSR, ALL_USER_ROLES.EXCHANGE_STAFF,
            ALL_USER_ROLES.EXCHANGE_SUPERVISOR
        ].includes(activeRoleName,);
    }, [activeRoleName]);

    const isValidStatus = useMemo(() => {
        return [
            ApplicationStatus.EligibilityReceived,
            ApplicationStatus.PartiallyEnrolled,
            ApplicationStatus.EnrolledOrActive,
            ApplicationStatus.Closed,
        ].includes(application.applicationStatus as ApplicationStatus);
    }, [application.applicationStatus]);

    const hasPermission = (permission: string): boolean => userPermissionList.includes(permission);
    const hasPermissionViewSSAP = hasPermission('SSAP_VIEW');

    const showApplicationSummary = useMemo(() => {
        return (
            hasPermissionViewSSAP && isValidStatus
        );
    }, [hasPermissionViewSSAP, isValidStatus]);

    const showEligibilityWorkSheet = useMemo(() => {
        return (
            !application.isNonFinancial &&
            isValidStateCode &&
            isValidRole &&
            Number(application.coverageYear) >= COVERAGE_YEAR &&
            isValidStatus
        );
    }, [application, isValidStateCode, isValidRole, isValidStatus]);

    return ({
        showApplicationSummary,
        showEligibilityWorkSheet
    });
};
